import React, { useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Target, Users, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const About = () => {
  const videoRef = useRef(null);

  useEffect(() => {
    // Ensure video plays when component mounts
    if (videoRef.current) {
      videoRef.current.play().catch(error => {
        console.log('Autoplay was prevented:', error);
      });
    }
  }, []);

  // Prevent right-click on video
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };
  return (
    <div className="bg-gray-950 text-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-b from-gray-900 to-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Aether Machining</h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Precision CNC machining excellence for aerospace, defense, and industrial applications
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">Who We Are</h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Aether Machining Inc. is a precision CNC machining company specializing in aerospace-grade manufacturing. 
                Located in Anaheim, California, we serve aerospace, defense, and industrial clients who demand 
                the highest levels of quality and precision.
              </p>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Our AS9100 Rev. D certified quality management system ensures that every component we produce 
                meets the stringent requirements of mission-critical applications. We combine advanced CNC technology 
                with experienced craftsmanship to deliver exceptional results.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">AS9100 Rev. D Certified</h3>
                    <p className="text-gray-400">Full aerospace quality compliance and traceability</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Precision Expertise</h3>
                    <p className="text-gray-400">Tight tolerances and complex geometries are our specialty</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Customer Focused</h3>
                    <p className="text-gray-400">Dedicated to exceeding customer expectations every time</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <video
                ref={videoRef}
                src="https://customer-assets.emergentagent.com/job_aerospace-cnc/artifacts/dngzbxrn_20240518_125718.mp4"
                loop
                muted
                autoPlay
                playsInline
                controlsList="nodownload nofullscreen noremoteplayback"
                disablePictureInPicture
                onContextMenu={handleContextMenu}
                className="rounded-lg shadow-2xl w-full pointer-events-none select-none"
                preload="auto"
                style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
              >
                Your browser does not support the video tag.
              </video>
              {/* Transparent overlay to prevent interaction */}
              <div 
                className="absolute inset-0 rounded-lg cursor-default"
                onContextMenu={handleContextMenu}
                style={{ userSelect: 'none' }}
              ></div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              These principles guide everything we do at Aether Machining
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-gray-950 border-gray-800 p-6 text-center hover:border-red-600 transition-all duration-300">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">Quality First</h3>
              <p className="text-gray-400">
                Uncompromising commitment to precision and excellence in every component
              </p>
            </Card>

            <Card className="bg-gray-950 border-gray-800 p-6 text-center hover:border-red-600 transition-all duration-300">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">Precision Focus</h3>
              <p className="text-gray-400">
                Attention to detail and tight tolerances in everything we manufacture
              </p>
            </Card>

            <Card className="bg-gray-950 border-gray-800 p-6 text-center hover:border-red-600 transition-all duration-300">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">Customer Partnership</h3>
              <p className="text-gray-400">
                Building long-term relationships through exceptional service and reliability
              </p>
            </Card>

            <Card className="bg-gray-950 border-gray-800 p-6 text-center hover:border-red-600 transition-all duration-300">
              <div className="w-16 h-16 bg-red-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-red-500" />
              </div>
              <h3 className="!font-semibold !text-xl mb-3 !text-[#E5E6E5]">Continuous Improvement</h3>
              <p className="text-gray-400">
                Always advancing our capabilities and processes for better results
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Industries We Serve</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Providing precision machining solutions across critical industries
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="bg-gray-900 border-gray-800 p-8">
              <h3 className="!font-semibold !text-2xl mb-4 !text-[#E5E6E5]">Aerospace</h3>
              <p className="text-gray-400 mb-4">
                Flight-critical components and assemblies for commercial and military aircraft, 
                meeting the most demanding aerospace standards.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Aircraft structural components</span>
                </li>
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Engine and propulsion parts</span>
                </li>
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Landing gear components</span>
                </li>
              </ul>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-8">
              <h3 className="!font-semibold !text-2xl mb-4 !text-[#E5E6E5]">Defense</h3>
              <p className="text-gray-400 mb-4">
                Mission-critical components for defense systems, with full traceability and 
                compliance with military specifications.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Weapons system components</span>
                </li>
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Vehicle and equipment parts</span>
                </li>
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Communication hardware</span>
                </li>
              </ul>
            </Card>

            <Card className="bg-gray-900 border-gray-800 p-8">
              <h3 className="!font-semibold !text-2xl mb-4 !text-[#E5E6E5]">Industrial</h3>
              <p className="text-gray-400 mb-4">
                High-precision components for industrial equipment and machinery requiring 
                exceptional quality and reliability.
              </p>
              <ul className="space-y-2">
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Automation equipment parts</span>
                </li>
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Hydraulic and pneumatic components</span>
                </li>
                <li className="flex items-center space-x-2 text-gray-300">
                  <span className="text-red-500">•</span>
                  <span>Custom machine parts</span>
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-900/20 to-gray-900/20 border-t border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Partner With Us</h2>
          <p className="text-xl text-gray-300 mb-8">
            Experience the Aether Machining difference. Contact us today to discuss your precision machining needs.
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <Link to="/contact">
              <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-md font-semibold transition-all duration-300">
                Request a Quote
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-gray-600 text-white hover:bg-gray-800 px-8 py-3 rounded-md font-semibold">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>);

};

export default About;